/**
 * coachMarks.ts — v2 optimistic client cache for the guided-tour system.
 *
 * The server (server/models/User.js `tours` + `journey`) is the truth,
 * fetched once at login on `/auth/me`. This module is the fast,
 * offline-tolerant mirror: every write lands here first (so a second tab or
 * an instant reload sees it immediately), then a PUT goes out in the
 * background (see lib/coachJourney.ts). `synced: false` on a record is the
 * retry signal — `syncCoachState` flushes it once per session.
 *
 * Namespaced by `user.id`, same reasoning as v1: localStorage is
 * device-local and account-blind, and two people sharing a browser is a
 * real pattern for our audience.
 *
 *   { "<userId>": {
 *       "tours":      { "home": { status, version, lastStepIndex?, completedAt?, synced } },
 *       "milestones": { "first-app": { at, synced } } } }
 *
 * v1's `fd:coach-marks` is read-through (see the LEGACY_* section below):
 * an un-promoted `completed` record still suppresses auto-show until
 * `syncCoachState` has had a chance to PUT it to the server and delete the
 * local copy.
 */

export const COACH_CACHE_KEY = 'fd:coach';
export const LEGACY_COACH_MARKS_KEY = 'fd:coach-marks';

export type TourStatus = 'completed' | 'dismissed';

export interface TourRecordDTO {
  status: TourStatus;
  version: number;
  lastStepIndex?: number;
  completedAt?: string;
}

export interface CachedTourRecord extends TourRecordDTO {
  synced: boolean;
}

export interface CoachUserCache {
  tours: Record<string, CachedTourRecord>;
  milestones: Record<string, { at: string; synced: boolean }>;
}

type CoachCacheStore = Record<string, CoachUserCache>;

// Cap distinct accounts on a shared browser. Object key order is insertion
// order in JS, so the oldest OTHER user (never the one writing right now)
// is evicted first.
const MAX_USERS = 5;

function emptyUserCache(): CoachUserCache {
  return { tours: {}, milestones: {} };
}

function readCacheStore(): CoachCacheStore {
  try {
    const raw = window.localStorage.getItem(COACH_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {}; // corrupt JSON / storage unavailable → treat as empty
  }
}

function writeCacheStore(store: CoachCacheStore): void {
  try {
    window.localStorage.setItem(COACH_CACHE_KEY, JSON.stringify(store));
  } catch {
    /* ignore — best-effort, private mode / quota */
  }
}

function pruneCache(store: CoachCacheStore, currentUserId: string): CoachCacheStore {
  const keys = Object.keys(store);
  if (keys.length <= MAX_USERS) return store;
  const others = keys.filter((k) => k !== currentUserId);
  while (Object.keys(store).length > MAX_USERS && others.length > 0) {
    delete store[others.shift() as string];
  }
  return store;
}

function withUserCache(userId: string, mutate: (user: CoachUserCache) => void): void {
  try {
    const store = readCacheStore();
    const user = store[userId] || emptyUserCache();
    mutate(user);
    store[userId] = user;
    writeCacheStore(pruneCache(store, userId));
  } catch {
    /* best-effort — a cache write must never throw into the caller */
  }
}

/** The full cache bucket for `userId`, or an empty one if none exists yet. */
export function readUserCache(userId: string): CoachUserCache {
  if (!userId) return emptyUserCache();
  try {
    return readCacheStore()[userId] || emptyUserCache();
  } catch {
    return emptyUserCache();
  }
}

/** Write (or overwrite) a tour/nudge record optimistically, before the PUT
 *  that will confirm it has landed. */
export function writeCachedTour(userId: string, tourId: string, rec: CachedTourRecord): void {
  if (!userId) return;
  withUserCache(userId, (user) => {
    user.tours[tourId] = rec;
  });
}

/** Drop a cached tour/nudge record. Used when the server's tours map (the
 *  truth) no longer has it — a staff reset (`reset-onboarding`) or a manual
 *  clear — so a stale, already-synced copy cannot keep blocking a tour. */
export function deleteCachedTour(userId: string, tourId: string): void {
  if (!userId) return;
  withUserCache(userId, (user) => {
    delete user.tours[tourId];
  });
}

/** Flip a cached tour record's `synced` flag once its PUT has confirmed
 *  (or once the server has told us it will never accept it). */
export function markTourSynced(userId: string, tourId: string): void {
  if (!userId) return;
  withUserCache(userId, (user) => {
    const rec = user.tours[tourId];
    if (rec) rec.synced = true;
  });
}

export function writeCachedMilestone(userId: string, name: string, synced: boolean): void {
  if (!userId) return;
  withUserCache(userId, (user) => {
    user.milestones[name] = { at: new Date().toISOString(), synced };
  });
}

export function markMilestoneSynced(userId: string, name: string): void {
  if (!userId) return;
  withUserCache(userId, (user) => {
    const rec = user.milestones[name];
    if (rec) rec.synced = true;
  });
}

/** Called from AuthContext.logout(): wipes the v2 cache only. The v1 legacy
 *  key is left alone — for whoever logs into this shared browser next, its
 *  presence is still their own retry signal to promote to the server on
 *  their own first sync. */
export function clearCoachCache(): void {
  try {
    window.localStorage.removeItem(COACH_CACHE_KEY);
  } catch {
    /* ignore — best-effort */
  }
}

// ---------------------------------------------------------------------------
// v1 read-through / promotion
// ---------------------------------------------------------------------------

export interface LegacyTourRecord {
  v: number;
  seenAt: string;
  lastStep: number;
  completed: boolean;
}

interface LegacyUserRecord {
  visits?: Record<string, number>;
  tours: Record<string, LegacyTourRecord>;
}

type LegacyStore = Record<string, LegacyUserRecord>;

function readLegacyStore(): LegacyStore {
  try {
    const raw = window.localStorage.getItem(LEGACY_COACH_MARKS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeLegacyStore(store: LegacyStore): void {
  try {
    window.localStorage.setItem(LEGACY_COACH_MARKS_KEY, JSON.stringify(store));
  } catch {
    /* ignore — best-effort */
  }
}

/** Every v1 tour record for `userId`, or `{}` if it never had any. */
export function readLegacyTours(userId: string): Record<string, LegacyTourRecord> {
  if (!userId) return {};
  try {
    return readLegacyStore()[userId]?.tours || {};
  } catch {
    return {};
  }
}

/** Drop one promoted (or abandoned) legacy tour record. Drops the user's
 *  whole bucket once its last tour is gone, and the storage key entirely
 *  once no user has any left — so a fully-promoted browser leaves nothing
 *  for `syncCoachState` to keep re-checking on every session. */
export function deleteLegacyTour(userId: string, tourId: string): void {
  if (!userId) return;
  try {
    const store = readLegacyStore();
    const user = store[userId];
    if (!user) return;
    delete user.tours[tourId];
    if (Object.keys(user.tours).length === 0) {
      delete store[userId];
    }
    if (Object.keys(store).length === 0) {
      window.localStorage.removeItem(LEGACY_COACH_MARKS_KEY);
      return;
    }
    writeLegacyStore(store);
  } catch {
    /* best-effort */
  }
}

/** v1 → v2 shape. `completed: false` (a dismissed run) maps to `dismissed`
 *  — v1 never persisted any other reason a run could end. */
export function legacyToRecord(r: LegacyTourRecord): TourRecordDTO {
  return {
    status: r.completed ? 'completed' : 'dismissed',
    version: r.v,
    lastStepIndex: r.lastStep,
    completedAt: r.seenAt,
  };
}
